import { audit, workflow } from '../api/endpoints';
import type { AuditEvent } from '../api/types';
import { useAsync } from '../hooks';
import { Card, Column, DataTable, Empty, ErrorBox, fmtDate, PageHeader } from '../components/ui';
import { apiList } from '../api/client';
import { useState } from 'react';

const ADMIN_LINKS = [
  { href: '/admin/', title: 'Django Admin', desc: 'Стандартная админ-панель backend' },
  { href: '/api/v1/schema/swagger/', title: 'Swagger UI', desc: 'Интерактивная документация API' },
  { href: '/api/v1/schema/redoc/', title: 'ReDoc', desc: 'OpenAPI-документация' },
  { href: '/api/v1/health/', title: 'Health check', desc: 'Состояние backend' },
];

const ADMIN_ENDPOINTS = [
  { path: '/workflow/workflow-rule/', title: 'Правила workflow' },
  { path: '/workflow/business-calendar/', title: 'Бизнес-календари' },
  { path: '/workflow/exception-approval/', title: 'Согласования исключений' },
  { path: '/identity/role/', title: 'Роли' },
  { path: '/finance/price-policy/', title: 'Ценовые политики' },
];

export function AdminPage() {
  const auditList = useAsync<AuditEvent[]>(() => audit.events({}), []);
  const [preview, setPreview] = useState<{ title: string; rows: unknown[]; error?: unknown } | null>(null);

  const loadList = async (path: string, title: string) => {
    try {
      const rows = await apiList(path);
      setPreview({ title, rows });
    } catch (e) {
      setPreview({ title, rows: [], error: e });
    }
  };

  const auditCols: Column<AuditEvent>[] = [
    { key: 'created_at', title: 'Время', render: (r) => fmtDate(r.created_at) },
    { key: 'action', title: 'Действие', render: (r) => r.action ?? '—' },
    { key: 'entity_type', title: 'Сущность', render: (r) => r.entity_type ?? '—' },
    { key: 'actor', title: 'Актор', render: (r) => (r.actor != null ? String(r.actor) : '—') },
  ];

  return (
    <div>
      <PageHeader title="Администрирование" />

      <Card title="Ссылки">
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {ADMIN_LINKS.map((l) => (
            <li key={l.href} style={{ marginBottom: 6 }}>
              <a href={l.href} target="_blank" rel="noreferrer">{l.title}</a>
              <span className="muted"> — {l.desc}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Справочники и правила (просмотр)">
        <div className="toolbar">
          {ADMIN_ENDPOINTS.map((e) => (
            <button key={e.path} className="btn small" onClick={() => void loadList(e.path, e.title)}>
              {e.title}
            </button>
          ))}
        </div>
        {preview && (
          <div style={{ marginTop: 8 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{preview.title}</h3>
            {preview.error ? <ErrorBox error={preview.error} /> :
              preview.rows.length === 0 ? <Empty text="Пусто" /> :
              <pre className="json-block">{JSON.stringify(preview.rows.slice(0, 20), null, 2)}</pre>}
          </div>
        )}
      </Card>

      <Card title="Журнал аудита" actions={<button className="btn" onClick={auditList.reload}>Обновить</button>}>
        <ErrorBox error={auditList.error} onRetry={auditList.reload} />
        <DataTable columns={auditCols} rows={auditList.data ?? []} empty="Событий аудита нет или раздел недоступен" />
      </Card>
    </div>
  );
}
